import React, { Suspense, lazy } from "react"
import { Redirect, Route, Switch } from "react-router-dom"

import { startServer } from "../ApiStubs"
import Contact from "../components/pages/Contact"
import NotFound from "../components/pages/NotFound"
import Footer from "../components/template-parts/Footer"
import GlobalSnackbar from "../components/template-parts/GlobalSnackbar"
import Header from "../components/template-parts/Header"
import Loading from "../components/template-parts/Loading"
import ReviewAlert from "../components/template-parts/reviews/ReviewAlert"
import { store } from "../store/store"
import { withDynamicApp } from "./withDynamicApp"
import Reload from "./Reload"
import { lessonTypes } from "../content"
import { FEATURE_FLAGS } from "../config"
import { useSelectorTyped } from "../store/configureStore"

if (process.env.REACT_APP_ENV === "test" && !(window as any).Cypress) {
  startServer()
}

const TX_ROAD_TEST_CONTENT_ID = lessonTypes["road-test"].stateMeta.tx
  .additionalContentId as string

const AsyncHowToGetLicense = lazy(
  () => import("../components/pages/HowToGetLicense"),
)
const AsyncAdmin = lazy(() => import("../components/pages/Admin"))
const AsyncPrep = lazy(() => import("../components/pages/Prep"))
const AsyncOnlineCourses = lazy(() => import("../components/pages/TakeCourse"))
const AsyncProfile = lazy(() => import("../components/pages/Account"))
const AsyncPartnerProfile = lazy(
  () => import("../components/pages/PartnerProfile"),
)
const AsyncLocationSearch = lazy(
  () => import("../components/pages/LocationSearch"),
)
const AsyncCourseComplete = lazy(
  () => import("../components/pages/CourseComplete"),
)
const AsyncInstructor = lazy(() => import("../components/pages/Instructor"))
const AsyncBookLesson = lazy(
  () => import("../components/template-parts/booking/Book"),
)
const AsyncLessonReport = lazy(
  () => import("../components/pages/StudentReportSharedPage"),
)
const AsyncDmvTestLesson = lazy(
  () => import("../components/pages/DmvTestLesson"),
)
const AsyncLocations = lazy(() => import("../components/pages/Locations"))
const AsyncInstructors = lazy(() => import("../components/pages/Instructors"))

const AsyncPricing = lazy(() => import("../components/pages/Pricing"))
const AsyncPricingEmbed = lazy(() => import("../components/pages/PricingEmbed"))
const AsyncBookComplete = lazy(
  () => import("../components/template-parts/booking/Complete"),
)
const AsyncPaymentRedirect = lazy(
  () => import("../components/template-parts/checkout/HandlePaymentRedirect"),
)
const AsyncLocationPackageLanding = lazy(
  () => import("../components/pages/LocationPackageLanding"),
)
const AsyncCheckout = lazy(() => import("../components/pages/Checkout"))
const AsyncCartRedirect = lazy(
  () => import("../components/template-parts/checkout/CartFromPath"),
)
const AsyncCartRedirectV2 = lazy(
  () => import("../components/template-parts/checkout/CartFromPathV2"),
)
const AsyncOrder = lazy(() => import("../components/pages/Order"))
const AsyncOrgRegistration = lazy(
  () => import("../components/template-parts/OrgRegistration"),
)
const AsyncPage = lazy(() => import("../components/pages/Page"))
const AsyncImpersonate = lazy(
  () => import("../components/template-parts/Impersonate"),
)

const showHeader = () => {
  if (window.location.href.includes("/courses/complete")) {
    return true
  }

  if (
    !window.location.href.includes("/admin/") &&
    !window.location.href.includes("/courses/") &&
    !window.location.href.includes("/pricing-embed")
  ) {
    return true
  }

  return false
}

const showFooter = () => {
  if (window.location.href.includes("/courses/complete")) {
    return true
  }

  if (
    !window.location.href.includes("/courses/") &&
    !window.location.href.includes("/pricing-embed")
  ) {
    return true
  }

  return false
}

const getWrapperStyle = () => {
  if (
    window.location.href.includes("/pricing-embed") ||
    window.location.href.indexOf("/book-lesson/") > 0 ||
    window.location.href.indexOf("/courses/") > 0
  ) {
    return { marginTop: 0 }
  }

  return {}
}

const App = () => {
  const userId = useSelectorTyped((state) => state.firebase.auth.uid)
  return (
    <>
      <>
        {showHeader() ? <Header isConnected={true} /> : null}
        <div className="main-wrapper" style={getWrapperStyle()}>
          <ReviewAlert />
          <Suspense fallback={<Loading />}>
            <Switch>
              <Route path="/admin">
                <AsyncAdmin />
              </Route>
              <Route exact path="/book-lesson/:slotId/5">
                <AsyncBookComplete />
              </Route>
              <Route exact path="/book-lesson/:slotId/:step?">
                <AsyncBookLesson />
              </Route>
              <Route
                exact
                path="/:state/location/:locationId/instructor/:instructorId"
              >
                <AsyncInstructor />
              </Route>
              <Route
                exact
                path="/location/:locationId/instructor/:instructorId"
              >
                <AsyncInstructor />
              </Route>
              <Route exact path="/:state/instructor/:instructorId/:type">
                <AsyncInstructor />
              </Route>
              <Route exact path="/instructor/:instructorId/:type">
                <AsyncInstructor />
              </Route>
              <Route exact path="/instructor/:instructorId">
                <AsyncInstructor />
              </Route>
              <Route exact path="/locations">
                <AsyncLocations />
              </Route>
              <Route exact path="/:state/location-search/:id/:type">
                <AsyncLocationSearch />
              </Route>
              <Route exact path="/location-search/:id/:type">
                <AsyncLocationSearch />
              </Route>
              <Route exact path="/:state/location-search/:id">
                <AsyncLocationSearch />
              </Route>
              <Route exact path="/location-search/:id">
                <AsyncLocationSearch />
              </Route>
              <Route exact path="/:state/location-search">
                <AsyncLocationSearch />
              </Route>
              <Route exact path="/location-search">
                <AsyncLocationSearch />
              </Route>
              <Route exact path="/packages">
                <AsyncLocationPackageLanding />
              </Route>
              <Route exact path="/dmv-certified-instructors">
                <Redirect to="/" />
              </Route>
              <Route exact path="/drivers-ed/order-de-certificate">
                <Redirect to="/courses/complete/drivers-ed-california" />
              </Route>
              <Route exact path="/drivers-ed/order-certificate">
                <Redirect to="/courses/complete/drivers-ed-california" />
              </Route>
              <Route
                exact
                path="/content/terms-of-service"
                render={() => (
                  <Redirect
                    to={{
                      pathname: "/article/terms-of-service",
                      state: { status: 301 },
                    }}
                  />
                )}
              />
              <Route
                exact
                path="/content/privacy-notice"
                render={() => (
                  <Redirect
                    to={{
                      pathname: "/article/privacy-notice",
                      state: { status: 301 },
                    }}
                  />
                )}
              />
              <Route exact path="/articles/:category?">
                <Reload />
              </Route>
              <Route exact path="/article/:slug">
                <Reload />
              </Route>
              <Route exact path="/contact">
                <Contact />
              </Route>
              <Route exact path="/profile/lessons">
                <AsyncProfile />
              </Route>
              {FEATURE_FLAGS.allowLessonNotifications.isEnabled(userId) && (
                <Route exact path="/profile/lesson-notifications">
                  <AsyncProfile />
                </Route>
              )}
              <Route exact path="/profile/courses">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile/edit">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile/mfa">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile/reports">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile/drives">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile/documents">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile">
                <AsyncProfile />
              </Route>
              <Route exact path="/profile/voucher/:code?">
                <AsyncProfile />
              </Route>
              <Route exact path="/partner/voucher/:code">
                <AsyncPartnerProfile />
              </Route>
              <Route exact path="/dmv-test-lesson">
                <AsyncDmvTestLesson />
              </Route>
              <Route path="/prep">
                <AsyncPrep />
              </Route>
              <Route exact path="/careers">
                <Reload />
              </Route>
              <Route path="/lesson-report/:sharedReportId">
                <AsyncLessonReport />
              </Route>
              <Route path="/Driving-Lessons">
                <Redirect to="/" />
              </Route>
              <Route path="/coastline-academy-parent">
                <Redirect to="/" />
              </Route>
              <Route exact path="/how-to-get-california-drivers-license/:step?">
                <AsyncHowToGetLicense />
              </Route>
              <Route exact path="/how-to-get-drivers-license">
                <AsyncHowToGetLicense />
              </Route>
              <Route exact path="/instructors">
                <AsyncInstructors />
              </Route>
              <Route exact path="/gift-card-partner">
                <Redirect to="/voucher-partner" />
              </Route>
              <Route exact path="/courses/complete/:courseSlug/:path?">
                <AsyncCourseComplete />
              </Route>
              <Route exact path="/courses/drivers-ed-2020">
                <Redirect to="/courses/drivers-ed-california" />
              </Route>
              <Route exact path="/courses/:courseSlug/:lectureId?">
                <AsyncOnlineCourses />
              </Route>
              <Route exact path="/:state/course/:courseType">
                <Reload />
              </Route>
              <Route exact path="/pricing/:query?">
                <AsyncPricing />
              </Route>
              <Route exact path="/pricing-embed">
                <AsyncPricingEmbed />
              </Route>
              <Route exact path="/checkout/:state/payment-redirect">
                <AsyncPaymentRedirect />
              </Route>
              <Route
                exact
                path="/checkout-v2/:state/:priceSchemeId/:packageId/:courseId?"
              >
                <AsyncCartRedirectV2 />
              </Route>
              <Route exact path="/checkout/:state/:packageId/course/:courseId">
                <AsyncCartRedirect />
              </Route>
              <Route exact path="/checkout/:state/:zipcode/:packageId">
                <AsyncCartRedirect />
              </Route>
              <Route
                exact
                path="/checkout/:state/:zipcode/:packageId/course/:courseId"
              >
                <AsyncCartRedirect />
              </Route>
              <Route exact path="/checkout/:state/:packageId">
                <AsyncCartRedirect />
              </Route>
              <Route exact path="/checkout/:step?">
                <AsyncCheckout />
              </Route>
              <Route exact path="/order/:cartId">
                <AsyncOrder />
              </Route>
              <Route exact path="/profile/org">
                <AsyncOrgRegistration />
              </Route>
              <Route exact path="/fleet">
                <AsyncPage uid="coastline-fleet-training" />
              </Route>
              <Route exact path="/road-test-requirements/tx">
                <AsyncPage uid={TX_ROAD_TEST_CONTENT_ID} showPageTitle />
              </Route>
              <Route
                path="/timetosell"
                component={() => {
                  window.location.href =
                    "https://coastlineacademy.zendesk.com/sales/app/capture-form"
                  return null
                }}
              />
              <Route path="/team-guide">
                {() => {
                  window.location.href =
                    "https://sites.google.com/coast.ai/guide/guide"
                  return null
                }}
              </Route>
              <Route path="/managers-guide">
                {() => {
                  window.location.href =
                    "https://sites.google.com/coast.ai/regionalmanagerguide/home"
                  return null
                }}
              </Route>
              <Route path="/impersonate/:token">
                <AsyncImpersonate />
              </Route>
              <Route path="*">
                <NotFound />
              </Route>
            </Switch>
          </Suspense>
        </div>
      </>
      <GlobalSnackbar />
      {showFooter() ? <Footer /> : null}
    </>
  )
}

// This is for integration tests so it has direct access to the Redux store
if ((window as any).Cypress && process.env.REACT_APP_ENV === "test") {
  window.store = store
}
export default withDynamicApp(App)
